import React, { type HTMLAttributes } from 'react';
import classNames from 'classnames';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import {
  ButtonWrapper,
  ParagraphsRenderer,
  VariableRatiosPicture,
} from '../../../components';
import useLayoutVariables from '../../../hooks/useLayoutVariables';
import type { NodeLandingPage as Type } from '../../../types/uncinc_profile/nodes';

import './style.scss';

interface LandingPageProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const NodeLandingPage: React.FC<LandingPageProps> = ({ entity, className }) => {
  const {
    heroImageBreakpointAspectRatios,
    heroTextColClasses,
    heroImageColClasses,
  } = useLayoutVariables('landingPage');

  const landingPage = entity.toJS();
  const heroTitle = landingPage.title[0].value;
  const heroBody = landingPage.body[0]?.processed;
  const heroImage = landingPage.field_media_item[0];

  const link = landingPage.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const linkSecondary = landingPage.field_link_secondary[0];
  const linkSecondaryTitle = linkSecondary?.title;
  const linkSecondaryUrl = linkSecondary?.alias || linkSecondary?.uri || '';
  const classes = classNames('Node', 'NodeLandingPage', className);

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="NodeLandingPage__hero">
        <Container>
          <div className="row">
            <div className={heroImageColClasses}>
              <div className="NodeLandingPage__hero__image-wrapper">
                <div className="NodeLandingPage__hero__image">
                  <VariableRatiosPicture
                    image={heroImage}
                    aspectRatios={heroImageBreakpointAspectRatios}
                  />
                </div>
              </div>
            </div>

            <div className={heroTextColClasses}>
              <div className="NodeLandingPage__hero__text-wrapper">
                <div className="NodeLandingPage__hero__text">
                  <h1 itemProp="title" className="NodeLandingPage__title">
                    {heroTitle}
                  </h1>

                  {heroBody && (
                    <WysiwygField
                      className="NodeLandingPage__body body--large"
                      property="schema:text"
                      html={heroBody}
                    />
                  )}

                  {(link || linkSecondary) && (
                    <div className="NodeLandingPage__links">
                      {link && (
                        <ButtonWrapper href={linkUrl}>
                          {linkTitle}
                        </ButtonWrapper>
                      )}
                      {linkSecondary && (
                        <ButtonWrapper href={linkSecondaryUrl} type="secondary">
                          {linkSecondaryTitle}
                        </ButtonWrapper>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <ParagraphsRenderer className="NodeLandingPage__paragraphs" paragraphs={entity.get('field_paragraphs')} />
    </article>
  );
};

export default NodeLandingPage;
